// pages/Clients/RoutePlay.jsx
import React, { useState, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import moment from "moment-timezone";

mapboxgl.accessToken = "pk.eyJ1IjoiYWxjb3Jha2UiLCJhIjoiY2tydnJub2JpMDllajJwb3h2NjdjbjFkdSJ9.6niQ3lJJ1OVEBMKj_BvFkA";

const RoutePlay = ({ isOpen, onClose, client }) => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedRoute, setHasLoadedRoute] = useState(false);
  const [noRoutes, setNoRoutes] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [distance, setDistance] = useState(0);

  const mapContainer = useRef(null);
  const map = useRef(null);
  const vehicleMarker = useRef(null);
  const popupRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  
  const playbackIndexRef = useRef(0);
  const playbackDataRef = useRef([]);
  const playbackTimerRef = useRef(null);
  const routeCoordsRef = useRef([]);
  const arrowFeaturesRef = useRef([]);
  const currentDistanceRef = useRef(0);
  const isPlayingRef = useRef(false);
  const speedRef = useRef(1);

  // Sync refs with state
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const calcDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  // Clear all map data
  const clearMapData = () => {
    if (map.current?.getSource("route")) {
      map.current.getSource("route").setData({ type: "FeatureCollection", features: [] });
    }
    if (map.current?.getSource("arrows")) {
      map.current.getSource("arrows").setData({ type: "FeatureCollection", features: [] });
    }
    
    routeCoordsRef.current = [];
    arrowFeaturesRef.current = [];
    
    if (vehicleMarker.current) {
      vehicleMarker.current.remove();
      vehicleMarker.current = null;
    }
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    if (startMarkerRef.current) {
      startMarkerRef.current.remove();
      startMarkerRef.current = null;
    }
    if (endMarkerRef.current) {
      endMarkerRef.current.remove();
      endMarkerRef.current = null;
    }
  };

  // Stop playback completely
  const stopPlayback = () => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    setIsPlaying(false);
    isPlayingRef.current = false;
  };

  // Reset everything
  const resetPlayback = () => {
    stopPlayback();
    clearMapData();
    playbackIndexRef.current = 0;
    currentDistanceRef.current = 0;
    setDistance(0);
    routeCoordsRef.current = [];
    arrowFeaturesRef.current = [];
  };

  // Draw route line from start to current index
  const drawRouteUpToIndex = (index) => {
    if (!map.current || index === 0) return;
    
    const coords = [];
    for (let i = 0; i <= index; i++) {
      const point = playbackDataRef.current[i];
      const lat = parseFloat(point?.lat);
      const lon = parseFloat(point?.lon);
      if (!isNaN(lat) && !isNaN(lon)) {
        coords.push([lon, lat]);
      }
    }
    
    routeCoordsRef.current = coords;
    const routeSource = map.current.getSource("route");
    if (routeSource) {
      routeSource.setData({
        type: "Feature",
        geometry: { type: "LineString", coordinates: coords }
      });
    }
  };

  // Update marker position
  const updateMarkerPosition = (index) => {
    const point = playbackDataRef.current[index];
    if (!point) return;
    
    const lat = parseFloat(point?.lat);
    const lon = parseFloat(point?.lon);
    if (isNaN(lat) || isNaN(lon)) return;
    
    const popupHTML = `
      <div class="playback-popup">
        <div class="popup-header">🚗 ${client?.regNum || client?.regnum || "Vehicle"}</div>
        <div class="popup-row"><span>⚡ Speed:</span><strong>${point?.speed || 0} km/h</strong></div>
      </div>
    `;

            // <div class="popup-row"><span>🕒 Time:</span><strong>${moment(point?.gtime).format("DD-MM-YYYY HH:mm:ss")}</strong></div>

    
    if (!popupRef.current) {
      popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: [0, -48] });
    }
    popupRef.current.setHTML(popupHTML);
    
    if (!vehicleMarker.current) {
      const markerEl = document.createElement("div");
      markerEl.className = "playback-marker";
      markerEl.innerHTML = `
        <div class="neo-marker">
          <div class="neo-pulse"></div>
          <div class="neo-pin"><div class="neo-center"></div></div>
        </div>
      `;
      vehicleMarker.current = new mapboxgl.Marker({ element: markerEl, anchor: "bottom" });
    }
    
    vehicleMarker.current
      .setLngLat([lon, lat])
      .setPopup(popupRef.current)
      .addTo(map.current);
    
    if (!popupRef.current.isOpen()) {
      popupRef.current.addTo(map.current);
    }
    
    // Center map on marker
    map.current.easeTo({ center: [lon, lat], duration: 300, zoom: 15, essential: true });
  };

  // Update arrows for the route
  const updateArrows = (index) => {
    if (!map.current || index < 1) return;
    
    const newArrows = [];
    for (let i = 1; i <= index; i++) {
      const prev = playbackDataRef.current[i - 1];
      const curr = playbackDataRef.current[i];
      
      const lat1 = parseFloat(prev?.lat);
      const lon1 = parseFloat(prev?.lon);
      const lat2 = parseFloat(curr?.lat);
      const lon2 = parseFloat(curr?.lon);
      
      if (isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) continue;
      
      const angle = ((Math.atan2(lat2 - lat1, lon2 - lon1) * 180) / Math.PI) + 90;
      const midpoint = [(lon1 + lon2) / 2, (lat1 + lat2) / 2];
      
      newArrows.push({
        type: "Feature",
        properties: { rotation: angle },
        geometry: { type: "Point", coordinates: midpoint }
      });
    }
    
    arrowFeaturesRef.current = newArrows;
    const arrowSource = map.current.getSource("arrows");
    if (arrowSource) {
      arrowSource.setData({
        type: "FeatureCollection",
        features: newArrows.slice(-100)
      });
    }
  };

  // Start playback from current index
  const startPlayback = () => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    
    const data = playbackDataRef.current;
    if (!map.current || !data.length || playbackIndexRef.current >= data.length) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      return;
    }
    
    setIsPlaying(true);
    isPlayingRef.current = true;
    
    const intervalTime = Math.max(50, 400 / speedRef.current);
    
    playbackTimerRef.current = setInterval(() => {
      if (!isPlayingRef.current) return;
      
      let idx = playbackIndexRef.current;
      if (idx >= data.length) {
        // Playback finished
        stopPlayback();
        return;
      }
      
      const point = data[idx];
      const lat = parseFloat(point?.lat);
      const lon = parseFloat(point?.lon);
      
      if (!isNaN(lat) && !isNaN(lon)) {
        // Update position
        updateMarkerPosition(idx);
        
        // Update route line
        drawRouteUpToIndex(idx);
        
        // Update arrows
        updateArrows(idx);
        
        // Update distance
        if (idx > 0) {
          const prev = data[idx - 1];
          const prevLat = parseFloat(prev?.lat);
          const prevLon = parseFloat(prev?.lon);
          if (!isNaN(prevLat) && !isNaN(prevLon)) {
            const segmentDist = calcDistance(prevLat, prevLon, lat, lon);
            currentDistanceRef.current += segmentDist;
            setDistance(currentDistanceRef.current);
          }
        }
      }
      
      playbackIndexRef.current++;
    }, intervalTime);
  };

  // Pause playback
  const pausePlayback = () => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    setIsPlaying(false);
    isPlayingRef.current = false;
  };

  // Load and start route
  const loadAndPlayRoute = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From Date and To Date");
      return;
    }
    
    const cbid = client?.cbid;
    if (!cbid) {
      alert("No CBID found for this client");
      return;
    }
    
    setIsLoading(true);
    setHasLoadedRoute(false);
    playbackDataRef.current = [];
    setNoRoutes(false);
    resetPlayback();
    
    const fromGMT = moment(fromDate)
      .subtract(5, "hours")
      .subtract(30, "minutes")
      .format("YYYY-MM-DD HH:mm:ss");
    
    const toGMT = moment(toDate)
      .subtract(5, "hours")
      .subtract(30, "minutes")
      .format("YYYY-MM-DD HH:mm:ss");
    
    const url = `https://alcobrakeadmin.com/v3/app/v2data/getallpingsv23?cbid=${cbid}&from=${fromGMT}&to=${toGMT}`;
    
    try {
      const res = await fetch(url);
      const json = await res.json();
      const dataArr = Array.isArray(json) ? json : (json.data || []);
      
      if (!dataArr.length) {
        setNoRoutes(true);
        setIsLoading(false);
        return;
      }
      
      playbackDataRef.current = dataArr;
      playbackIndexRef.current = 0;
      currentDistanceRef.current = 0;
      setDistance(0);
      
      // Add start and end markers
      const getLatLon = (p) => {
        const lat = parseFloat(p?.lat ?? p?.latitude);
        const lon = parseFloat(p?.lon ?? p?.lng ?? p?.longitude);
        return { lat, lon };
      };
      
      // Start marker
      const firstPoint = dataArr[0];
      if (firstPoint) {
        const { lat, lon } = getLatLon(firstPoint);
        if (!isNaN(lat) && !isNaN(lon)) {
          if (startMarkerRef.current) startMarkerRef.current.remove();
          const startEl = document.createElement("div");
          startEl.innerHTML = `<div style="font-size:22px;">🟢🏁</div>`;
          startMarkerRef.current = new mapboxgl.Marker({ element: startEl, anchor: "bottom" })
            .setLngLat([lon, lat])
            .addTo(map.current);
        }
      }
      
      // End marker
      const lastPoint = dataArr[dataArr.length - 1];
      if (lastPoint) {
        const { lat, lon } = getLatLon(lastPoint);
        if (!isNaN(lat) && !isNaN(lon)) {
          if (endMarkerRef.current) endMarkerRef.current.remove();
          const endEl = document.createElement("div");
          endEl.innerHTML = `<div style="font-size:22px;">🔴🏁</div>`;
          endMarkerRef.current = new mapboxgl.Marker({ element: endEl, anchor: "bottom" })
            .setLngLat([lon, lat])
            .addTo(map.current);
        }
      }
      
      setIsLoading(false);
      setHasLoadedRoute(true);
      startPlayback();
      
    } catch (err) {
      console.error(err);
      setNoRoutes(true);
      setIsLoading(false);
    }
  };

  // Restart from beginning
  const restartRoute = () => {
    if (playbackDataRef.current.length === 0) return;
    
    // Stop current playback
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    
    // Reset indices and distance
    playbackIndexRef.current = 0;
    currentDistanceRef.current = 0;
    setDistance(0);
    
    // Clear route and arrows from map
    if (map.current?.getSource("route")) {
      map.current.getSource("route").setData({ type: "FeatureCollection", features: [] });
    }
    if (map.current?.getSource("arrows")) {
      map.current.getSource("arrows").setData({ type: "FeatureCollection", features: [] });
    }
    
    routeCoordsRef.current = [];
    arrowFeaturesRef.current = [];
    
    // Start playback from beginning
    setIsPlaying(true);
    isPlayingRef.current = true;
    
    const intervalTime = Math.max(50, 400 / speedRef.current);
    
    playbackTimerRef.current = setInterval(() => {
      if (!isPlayingRef.current) return;
      
      let idx = playbackIndexRef.current;
      if (idx >= playbackDataRef.current.length) {
        stopPlayback();
        return;
      }
      
      const point = playbackDataRef.current[idx];
      const lat = parseFloat(point?.lat);
      const lon = parseFloat(point?.lon);
      
      if (!isNaN(lat) && !isNaN(lon)) {
        updateMarkerPosition(idx);
        drawRouteUpToIndex(idx);
        updateArrows(idx);
        
        if (idx > 0) {
          const prev = playbackDataRef.current[idx - 1];
          const prevLat = parseFloat(prev?.lat);
          const prevLon = parseFloat(prev?.lon);
          if (!isNaN(prevLat) && !isNaN(prevLon)) {
            const segmentDist = calcDistance(prevLat, prevLon, lat, lon);
            currentDistanceRef.current += segmentDist;
            setDistance(currentDistanceRef.current);
          }
        }
      }
      
      playbackIndexRef.current++;
    }, intervalTime);
  };

  // Change speed during playback - FIXED: Single click now works
  const handleSpeedChange = (newSpeed) => {
    // Update state
    setSpeed(newSpeed);
    speedRef.current = newSpeed;
    
    // If currently playing, restart timer with new speed
    if (isPlaying && playbackTimerRef.current) {
      // Store current index and distance
      const currentIndex = playbackIndexRef.current;
      const currentDist = currentDistanceRef.current;
      
      // Clear existing timer
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
      
      // Calculate new interval
      const intervalTime = Math.max(50, 400 / newSpeed);
      
      // Start new timer from current position
      playbackTimerRef.current = setInterval(() => {
        if (!isPlayingRef.current) return;
        
        let idx = playbackIndexRef.current;
        if (idx >= playbackDataRef.current.length) {
          stopPlayback();
          return;
        }
        
        const point = playbackDataRef.current[idx];
        const lat = parseFloat(point?.lat);
        const lon = parseFloat(point?.lon);
        
        if (!isNaN(lat) && !isNaN(lon)) {
          updateMarkerPosition(idx);
          drawRouteUpToIndex(idx);
          updateArrows(idx);
          
          if (idx > 0) {
            const prev = playbackDataRef.current[idx - 1];
            const prevLat = parseFloat(prev?.lat);
            const prevLon = parseFloat(prev?.lon);
            if (!isNaN(prevLat) && !isNaN(prevLon)) {
              const segmentDist = calcDistance(prevLat, prevLon, lat, lon);
              currentDistanceRef.current += segmentDist;
              setDistance(currentDistanceRef.current);
            }
          }
        }
        
        playbackIndexRef.current++;
      }, intervalTime);
    }
  };

  useEffect(() => {
    setHasLoadedRoute(false);
    playbackDataRef.current = [];
    setDistance(0);
    setSpeed(1);
  }, [isOpen]);

  // Map initialization
  useEffect(() => {
    if (!isOpen) return;
    if (!mapContainer.current) return;
    if (map.current) {
      map.current.resize();
      return;
    }
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/navigation-day-v1",
      center: [78.4867, 17.3850],
      zoom: 10,
      attributionControl: false
    });
    
    map.current.on("load", () => {
      map.current.addSource("route", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] }
      });
      
      map.current.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#1976d2", "line-width": 4 }
      });
      
      const arrowSVG = `
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <polygon points="20,4 34,34 20,26 6,34" fill="#00cfff" stroke="#ffffff" stroke-width="2"/>
        </svg>
      `;
      
      const arrowImg = new Image(80, 80);
      arrowImg.onload = () => {
        if (!map.current.hasImage("route-arrow")) {
          map.current.addImage("route-arrow", arrowImg);
        }
        
        map.current.addSource("arrows", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] }
        });
        
        map.current.addLayer({
          id: "arrows",
          type: "symbol",
          source: "arrows",
          layout: {
            "symbol-placement": "point",
            "icon-image": "route-arrow",
            "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.15, 10, 0.25, 15, 0.4, 18, 0.6],
            "icon-rotate": ["get", "rotation"],
            "icon-rotation-alignment": "map",
            "icon-allow-overlap": true,
            "icon-ignore-placement": true
          },
          paint: { "icon-opacity": 1 }
        });
      };
      
      arrowImg.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(arrowSVG);
    });
    
    return () => {
      stopPlayback();
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [isOpen]);
  
  // Cleanup on close
  const handleClose = () => {
    stopPlayback();
    onClose();
  };
  
  if (!isOpen) return null;
  
  const hasData = playbackDataRef.current.length > 0;
  
  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="routeplay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="routeplay-modal-header">
          <div>
            <h3>Route Playback - {client?.name || client?.client}</h3>
            <p>{client?.regNum || client?.regnum} • {client?.cbid}</p>
          </div>
          <button className="close-btn" onClick={handleClose}>✕</button>
        </div>
        
        <div className="routeplay-body">
          {/* Date Selection Controls */}
          <div className="routeplay-controls">
            <div className="control-row">
              <div className="date-field">
                <label>From Date & Time</label>
                <input
                  type="datetime-local"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="date-field">
                <label>To Date & Time</label>
                <input
                  type="datetime-local"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <button 
                className="load-btn" 
                onClick={loadAndPlayRoute} 
                disabled={isLoading || (isPlaying && !noRoutes)}
              >
                {isLoading ? "Loading..." : "Load & Play"}
              </button>
            </div>
            
            {distance > 0 && (
              <div className="distance-info">
                📍 Total Distance: {distance.toFixed(2)} km
              </div>
            )}
          </div>
          
          {/* Map */}
          <div className="routeplay-map-wrapper">
            {(isLoading || noRoutes) && (
              <div className="overlay-ui">
                <div className="glass-card">
                  {isLoading ? (
                    <>
                      <div className="spinner"></div>
                      <p>Loading GPS Playback...</p>
                    </>
                  ) : noRoutes ? (
                    <>
                      <h3>No Routes Found</h3>
                      <p>Try another time range</p>
                      <button onClick={() => setNoRoutes(false)}>OK</button>
                    </>
                  ) : null}
                </div>
              </div>
            )}
            <div ref={mapContainer} className="routeplay-map"></div>
          </div>
          
          {/* Playback Controls */}
          {hasLoadedRoute && <div className="playback-controls-bar">
            <div className="playback-buttons">
              {!isPlaying ? (
                <button 
                  className="control-btn play-control"
                  onClick={startPlayback}
                  disabled={!hasData || isLoading}
                >
                  ▶ Play
                </button>
              ) : (
                <button 
                  className="control-btn pause-control"
                  onClick={pausePlayback}
                >
                  ⏸ Pause
                </button>
              )}
              <button 
                className="control-btn stop-control"
                onClick={resetPlayback}
                disabled={!hasData}
              >
                ⏹ Stop
              </button>
              <button 
                className="control-btn restart-control"
                onClick={restartRoute}
                disabled={!hasData || isLoading}
              >
                ⟳ Restart
              </button>
            </div>
            
            <div className="speed-buttons">
              <span className="speed-label">Speed:</span>
              {[1, 2, 5, 10].map((s) => (
                <button
                  key={s}
                  className={`speed-btn ${speed === s ? "active" : ""}`}
                  onClick={() => handleSpeedChange(s)}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default RoutePlay;